import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertCircle, ArrowUpRight, Clock3, Loader2, MessageSquareText, RefreshCw, Search, ShieldCheck } from "lucide-react";
import type { WaitingReplyMessage } from "@shared/waiting-reply";
import { usePermissions } from "@/hooks/use-permissions";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { formatTime } from "./_shared";
import { invalidateWaitingReplyQueries } from "./_waiting-reply-invalidations";

const WAITING_REPLY_KEY = ["/api/app/inbox/waiting-reply/messages"];

export function formatWaitingDuration(waitingReplyAt: string, now = Date.now()) {
  const then = new Date(waitingReplyAt).getTime();
  if (!Number.isFinite(then)) return "—";
  const minutes = Math.floor(Math.max(0, now - then) / 60_000);
  const days = Math.floor(minutes / 1_440);
  const hours = Math.floor((minutes % 1_440) / 60);
  const remainder = minutes % 60;
  return [days ? `${days}d` : "", hours ? `${hours}h` : "", remainder ? `${remainder}m` : ""]
    .filter(Boolean)
    .join(" ") || "0m";
}

export function WaitingRepliesTab({
  propertyIds,
  onOpen,
}: {
  propertyIds: number[] | null;
  onOpen: (memberId: number, chatId: number) => void;
}) {
  const { isLoading: permissionsLoading, canView, canEdit } = usePermissions();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const canSeeMessages = !permissionsLoading && canView("messages");
  const canClearFlags = !permissionsLoading && canEdit("messages");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<WaitingReplyMessage | null>(null);
  const [clearError, setClearError] = useState("");
  const [minute, setMinute] = useState(() => Date.now());
  const submitting = useRef(false);

  useEffect(() => {
    const timer = window.setInterval(() => setMinute(Date.now()), 60_000);
    return () => window.clearInterval(timer);
  }, []);

  const query = useQuery<WaitingReplyMessage[]>({
    queryKey: WAITING_REPLY_KEY,
    queryFn: async () => {
      const response = await apiRequest("GET", "/api/app/inbox/waiting-reply/messages");
      return response.json() as Promise<WaitingReplyMessage[]>;
    },
    enabled: canSeeMessages,
    staleTime: 0,
    refetchInterval: canSeeMessages ? 10_000 : false,
  });

  const clearMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: async (row: WaitingReplyMessage) =>
      apiRequest("PATCH", `/api/app/member-chats/${row.chatId}/workflow`, { waitingReplyAt: null }),
    onSuccess: (_result, row) => {
      submitting.current = false;
      queryClient.setQueryData<WaitingReplyMessage[]>(WAITING_REPLY_KEY, (old) =>
        old?.filter((item) => item.chatId !== row.chatId),
      );
      invalidateWaitingReplyQueries(queryClient, row.memberId, row.chatId);
      setClearError("");
      setSelected(null);
      toast({ title: "Waiting flag cleared", description: `The flag on this message to ${row.memberName} was removed.` });
    },
    onError: (error) => {
      const message = parseErrorMessage(error, "Couldn't clear this flag. Please try again.");
      setClearError(message);
      submitting.current = false;
      toast({
        title: "Couldn't clear waiting flag",
        description: message,
        variant: "destructive",
      });
    },
  });

  const visibleRows = useMemo(() => {
    const allowed = propertyIds === null ? null : new Set(propertyIds);
    const term = search.trim().toLocaleLowerCase();
    return [...(query.data ?? [])]
      .filter((row) => allowed === null || (row.propertyId !== null && allowed.has(row.propertyId)))
      .filter((row) => !term || String(row.memberName ?? "").toLocaleLowerCase().includes(term) || String(row.preview ?? "").toLocaleLowerCase().includes(term))
      .sort((a, b) => {
        const dateDiff = new Date(a.waitingReplyAt).getTime() - new Date(b.waitingReplyAt).getTime();
        return dateDiff || a.chatId - b.chatId;
      });
  }, [query.data, propertyIds, search]);

  function submitClear(row: WaitingReplyMessage) {
    if (!canClearFlags || submitting.current || clearMutation.isPending) return;
    submitting.current = true;
    setClearError("");
    clearMutation.mutate(row);
  }

  function closeDialog(open: boolean) {
    if (!open && !clearMutation.isPending) {
      setSelected(null);
      setClearError("");
    }
  }

  const rowState = permissionsLoading || query.isLoading
    ? "loading"
    : !canSeeMessages
      ? "restricted"
      : query.isError
        ? "error"
        : visibleRows.length
          ? "rows"
          : (query.data?.length ?? 0) === 0
            ? "empty"
            : "filtered";

  return (
    <div className="h-full min-h-[300px] overflow-auto p-4 sm:p-6" data-testid="tab-content-waiting-replies">
      <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="flex items-center gap-2">
            <MessageSquareText className="h-4 w-4 text-primary" />
            <h2 className="text-base font-semibold text-foreground">Waiting for a reply</h2>
          </div>
          <p className="mt-1 text-sm text-muted-foreground">Messages awaiting a member’s reply, oldest first.</p>
        </div>
        <div className="relative w-full sm:max-w-xs">
          <Search aria-hidden="true" className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            aria-label="Search waiting replies"
            className="pl-9"
            placeholder="Search member or message"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            data-testid="input-waiting-replies-search"
          />
        </div>
      </div>

      <div className="overflow-x-auto rounded-lg border border-border bg-card">
        {rowState === "loading" && <p role="status" className="px-4 py-3 text-sm text-muted-foreground">Loading awaiting replies…</p>}
        <table className="w-full min-w-[760px] border-collapse text-left text-sm">
          <thead className="bg-muted/60 text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th scope="col" className="px-4 py-3 font-medium">Member / property</th>
              <th scope="col" className="px-4 py-3 font-medium">Flagged message</th>
              <th scope="col" className="px-4 py-3 font-medium">Marked at</th>
              <th scope="col" className="px-4 py-3 font-medium">Waiting</th>
              <th scope="col" className="px-4 py-3 text-right font-medium">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {rowState === "loading" ? (
              Array.from({ length: 3 }, (_, index) => (
                <tr key={index} aria-hidden="true">
                  <td colSpan={5} className="px-4 py-5">
                    <div className="h-4 max-w-[620px] animate-pulse rounded bg-muted" />
                  </td>
                </tr>
              ))
            ) : rowState === "rows" ? visibleRows.map((row) => (
              <tr
                key={row.chatId}
                tabIndex={0}
                className="cursor-pointer transition-colors hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary"
                onClick={() => onOpen(row.memberId, row.chatId)}
                onKeyDown={(event) => {
                  if (event.target !== event.currentTarget) return;
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    onOpen(row.memberId, row.chatId);
                  }
                }}
                data-testid={`row-waiting-reply-${row.chatId}`}
              >
                <td className="px-4 py-4 align-top">
                  <div className="font-medium text-foreground">{row.memberName}</div>
                  <div className="mt-1 text-xs text-muted-foreground">{row.propertyName || "Property unavailable"}</div>
                </td>
                <td className="max-w-[380px] px-4 py-4 align-top">
                  <p className="line-clamp-2 whitespace-pre-wrap break-words text-foreground/85">{row.preview || "No message preview"}</p>
                </td>
                <td className="whitespace-nowrap px-4 py-4 align-top text-muted-foreground">{formatTime(row.waitingReplyAt)}</td>
                <td className="whitespace-nowrap px-4 py-4 align-top">
                  <span className="inline-flex items-center gap-1.5 font-medium tabular-nums text-foreground">
                    <Clock3 className="h-3.5 w-3.5 text-muted-foreground" />
                    {formatWaitingDuration(row.waitingReplyAt, minute)}
                  </span>
                </td>
                <td className="px-4 py-4 align-top">
                  <div className="flex justify-end gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={(event) => {
                        event.stopPropagation();
                        onOpen(row.memberId, row.chatId);
                      }}
                      data-testid={`button-open-waiting-reply-${row.chatId}`}
                    >
                      Open <ArrowUpRight className="ml-1 h-3.5 w-3.5" />
                    </Button>
                    {canClearFlags && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-muted-foreground hover:text-foreground"
                        onClick={(event) => {
                          event.stopPropagation();
                          setClearError("");
                          setSelected(row);
                        }}
                        data-testid={`button-clear-waiting-flag-${row.chatId}`}
                      >
                        <ShieldCheck className="mr-1.5 h-3.5 w-3.5" />
                        Clear flag
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            )) : null}
          </tbody>
        </table>
        {rowState === "error" ? (
          <div role="alert" className="flex flex-col items-center gap-3 px-6 py-12 text-center">
            <AlertCircle className="h-5 w-5 text-destructive" />
            <div>
              <p className="font-medium text-foreground">Waiting replies couldn’t be loaded.</p>
              <p className="mt-1 text-sm text-muted-foreground">Check your connection and try again.</p>
            </div>
            <Button variant="outline" size="sm" onClick={() => query.refetch()} disabled={query.isFetching}>
              <RefreshCw className={`mr-2 h-3.5 w-3.5 ${query.isFetching ? "animate-spin" : ""}`} />
              Retry
            </Button>
          </div>
        ) : rowState === "empty" || rowState === "filtered" || rowState === "restricted" ? (
          <div className="flex flex-col items-center px-6 py-12 text-center">
            {rowState === "restricted" ? (
              <>
                <ShieldCheck className="mb-3 h-6 w-6 text-muted-foreground" />
                <p className="text-sm font-medium text-foreground">You don’t have access to Messages.</p>
              </>
            ) : (
              <>
                <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-muted text-muted-foreground">
                  <MessageSquareText className="h-5 w-5" />
                </div>
                <p className="text-sm font-medium text-foreground">
                  {rowState === "empty" ? "No messages awaiting a reply." : "No awaiting replies match your filters."}
                </p>
                {rowState === "filtered" && (
                  <p className="mt-1 text-xs text-muted-foreground">Try a different member name or message preview.</p>
                )}
              </>
            )}
          </div>
        ) : null}
      </div>

      <Dialog open={selected !== null} onOpenChange={closeDialog}>
        <DialogContent
          onInteractOutside={(event) => {
            if (clearMutation.isPending) event.preventDefault();
          }}
          onEscapeKeyDown={(event) => {
            if (clearMutation.isPending) event.preventDefault();
          }}
          data-testid="dialog-clear-waiting-flag"
        >
          <DialogHeader>
            <DialogTitle>Clear waiting flag?</DialogTitle>
            <DialogDescription>Remove only this message’s awaiting-reply flag.</DialogDescription>
          </DialogHeader>
          {selected && (
            <div className="space-y-3 text-sm">
              <p className="text-foreground">
                Remove the waiting-for-reply flag for <span className="font-semibold">{selected.memberName}</span>?
              </p>
              <blockquote className="rounded-md border-l-2 border-border bg-muted/50 px-3 py-2 text-muted-foreground">
                {selected.preview || "No message preview"}
              </blockquote>
              <p className="text-muted-foreground">Only the flag will be removed. This message and the conversation will remain unchanged.</p>
              {clearError && (
                <div role="alert" className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
                  {clearError}
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" disabled={clearMutation.isPending} onClick={() => closeDialog(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={!selected || !canClearFlags || clearMutation.isPending}
              onClick={() => selected && submitClear(selected)}
              data-testid="button-confirm-clear-waiting-flag"
            >
              {clearMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Clear flag
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
